import type { Item } from '@trips/shared/schema';
import { useEffect } from 'react';
import { syncNow } from '../data/sync';
import { api } from './api';
import { now } from './clock';

const MIN = 60_000;
/** While the app is open on a flight, ask the server to check it this often. */
const EVERY = 2 * MIN;

/**
 * Keeps a flight fresh while it's on screen: from three hours before departure until it's in.
 * Before takeoff that means the phone has the latest delay, taxi and filed flying time when
 * the signal goes; in the air with wifi it brings the plane's position. Without a connection
 * the requests just fail quietly and the on-phone estimate carries on.
 */
export function useLiveFlight(item: Item | undefined) {
  const id = item?.type === 'flight' ? item.id : null;
  const start = item?.startAt ?? null;
  const end = item?.endAt ?? null;
  useEffect(() => {
    if (!id || start == null) return;
    const active = () => {
      const t = now();
      return t > start - 3 * 60 * MIN && t < (end ?? start + 12 * 60 * MIN) + 30 * MIN;
    };
    const refresh = async () => {
      if (!active() || document.visibilityState !== 'visible' || !navigator.onLine) return;
      try {
        await api(`/api/flights/${encodeURIComponent(id)}/refresh`, { method: 'POST' });
        await syncNow();
      } catch {
        // Offline or the tracker isn't set up: the estimate on the phone carries on.
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), EVERY);
    const onBack = () => void refresh();
    window.addEventListener('online', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [id, start, end]);
}
